// Client for the Smart Import extraction endpoint (a Cloudflare Worker).
//
// The Worker owns the model, the system prompt and the provider API key, so no
// credential is ever shipped to the browser. This module deliberately uses bare
// fetch — NOT axiosInstance — so the admin Bearer token never leaves for the
// extraction service and the global loader interceptor stays out of
// long-running extraction calls.

const WORKER_URL = (
  process.env.REACT_APP_SMART_IMPORT_API_URL || ""
).replace(/\/+$/, "");
const MODEL_LABEL =
  process.env.REACT_APP_SMART_IMPORT_MODEL_LABEL ||
  "deepseek-v4.1-flash";

const MAX_ATTEMPTS = 3;
const RETRYABLE_STATUS = new Set([429, 502, 503, 504]);

export class LlmConfigError extends Error {}
export class LlmRequestError extends Error {}

export function isLlmConfigured() {
  return Boolean(WORKER_URL);
}

export function getLlmModelName() {
  return MODEL_LABEL;
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function readErrorMessage(response) {
  try {
    const body = await response.json();
    return {
      message: body?.error?.message || "",
      code: body?.error?.code || ""
    };
  } catch {
    return { message: "", code: "" };
  }
}

/**
 * Send one chunk to the extraction service and return the assistant JSON as a
 * string, so the existing parseJsonFromContent/parseLlmQuestions chain applies
 * unchanged.
 *
 * Retries transient failures; fails fast on config and cancellation errors.
 */
export async function extractQuestionsFromChunk({
  userContent,
  signal
}) {
  if (!isLlmConfigured()) {
    throw new LlmConfigError(
      "Smart Import is not configured. Set REACT_APP_SMART_IMPORT_API_URL and rebuild."
    );
  }

  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
    let response;
    try {
      response = await fetch(`${WORKER_URL}/v1/chat/completions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          messages: [{ role: "user", content: userContent }]
        }),
        signal
      });
    } catch (error) {
      if (signal?.aborted || error.name === "AbortError") {
        throw error;
      }
      if (attempt === MAX_ATTEMPTS) {
        throw new LlmRequestError(
          "Could not reach the Smart Import service. Check your connection and try again."
        );
      }
      await sleep(1000 * attempt);
      continue;
    }

    if (response.ok) {
      const data = await response.json();
      const questions = data?.questions;
      if (!Array.isArray(questions)) {
        if (attempt === MAX_ATTEMPTS) {
          throw new LlmRequestError(
            "The Smart Import service returned an unexpected response."
          );
        }
        continue;
      }
      return JSON.stringify({ questions });
    }

    const { message, code } = await readErrorMessage(response);

    if (response.status === 400 && code === "VISION_UNSUPPORTED") {
      throw new LlmRequestError("VISION_UNSUPPORTED");
    }
    if (response.status === 429 && attempt < MAX_ATTEMPTS) {
      await sleep(1000 * attempt);
      continue;
    }
    if (
      RETRYABLE_STATUS.has(response.status) &&
      attempt < MAX_ATTEMPTS
    ) {
      await sleep(1000 * attempt);
      continue;
    }

    throw new LlmRequestError(
      message ||
        `Smart Import request failed (${response.status}). Please try again.`
    );
  }

  throw new LlmRequestError(
    "The Smart Import service did not return a usable response."
  );
}

/** Pull the JSON object out of a reply (tolerates ```json fences). */
export function parseJsonFromContent(content) {
  let text = content.trim();
  const fence = text.match(/```(?:json)?\s*([\s\S]*?)```/);
  if (fence) text = fence[1].trim();

  const start = text.indexOf("{");
  const end = text.lastIndexOf("}");
  if (start === -1 || end === -1 || end <= start) {
    throw new LlmRequestError(
      "The Smart Import service did not return a JSON object."
    );
  }
  return JSON.parse(text.slice(start, end + 1));
}
